<template>
    <div class="twilio-voice-insights-tab row nav-padding">
        <div class="d-flex align-items-center justify-content-between">
            <div class="px-3">
                <div v-if="company">
                    <div>
                        <span class="_700">ID:</span> #{{ company.id }}
                    </div>
                    <div>
                        <span class="_700">Company:</span> {{ company.name }}
                    </div>
                </div>
            </div>

            <div class="d-flex justify-content-end px-3 pt-3">
                <div class="mr-2">
                    <company-autosuggest
                        v-model="company_id"
                        @company-selected="handleCompanySelected"/>
                </div>

                <div>
                    <el-button
                        v-if="company_id"
                        @click="clearFilters">
                        Reset
                    </el-button>
                </div>
            </div>
        </div>

        <div class="w-100 pt-5">
            <div v-if="company" class="p-3">
                <el-card class="box-card" v-loading="loading">
                    <div slot="header" class="clearfix">
                        <span>Twilio Voice Insights</span>
                    </div>

                    <div v-if="!settings && !loading">
                        <el-alert
                            v-if="error_message"
                            :title="error_message"
                            type="error"
                            :closable="false"
                            show-icon/>
                    </div>

                    <div v-if="settings">
                        <div class="text-item">
                            <span class="label">Carrier:</span>
                            <span class="value">{{ settings.carrier_name }}</span>
                        </div>
                        <div class="text-item">
                            <span class="label">Subaccount SID:</span>
                            <span class="value">{{ settings.carrier_sid }}</span>
                        </div>

                        <el-divider></el-divider>

                        <div class="text-item">
                            <span class="label">Advanced Features:</span>
                            <el-switch
                                :value="settings.advanced_features"
                                :disabled="updating"
                                active-color="#13ce66"
                                @change="handleToggle('advanced_features', $event)"/>
                            <span class="ml-3 text-muted">
                                Master switch for Voice Insights Advanced Features (billed per-minute by Twilio).
                            </span>
                        </div>

                        <div class="text-item mt-3">
                            <span class="label">Voice Trace:</span>
                            <el-switch
                                :value="settings.voice_trace"
                                :disabled="updating"
                                active-color="#13ce66"
                                @change="handleToggle('voice_trace', $event)"/>
                            <span class="ml-3 text-muted">
                                Captures detailed SIP traces for calls. Only takes effect when Advanced Features is on.
                            </span>
                        </div>
                    </div>
                </el-card>
            </div>

            <div v-else class="h-100 d-flex align-items-center">
                <div v-if="loading" class="w-100">
                    <h3 class="text-center">Loading...</h3>
                </div>
                <div v-else class="w-100 search-resource-text">
                    <div class="text-center">
                        <i class="fa fa-search"></i>
                        <p class="mb-0">Select a company to view Twilio Voice Insights settings</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CompanyAutosuggest from '../company-autosuggest'

export default {
    data() {
        return {
            company: null,
            company_id: null,
            settings: null,
            loading: false,
            updating: false,
            error_message: null,
        }
    },

    components: {
        CompanyAutosuggest,
    },

    methods: {
        clearFilters() {
            this.company_id = null
            this.company = null
            this.settings = null
            this.error_message = null
        },

        handleCompanySelected(company) {
            this.company = company
            this.fetchSettings()
        },

        fetchSettings() {
            if (!this.company) return
            this.loading = true
            this.settings = null
            this.error_message = null

            axios.get(`/api/admin/company/${this.company.id}/twilio-voice-insights`)
                .then(response => {
                    this.settings = response.data
                })
                .catch(error => {
                    this.error_message = error.response?.data?.message || 'Failed to fetch Twilio Voice Insights settings'
                })
                .finally(() => {
                    this.loading = false
                })
        },

        handleToggle(field, new_value) {
            if (!this.company || !this.settings) return

            const label = field === 'advanced_features' ? 'Advanced Features' : 'Voice Trace'
            const action = new_value ? 'enable' : 'disable'
            const warning = field === 'advanced_features' && new_value
                ? ' This adds Twilio per-minute billing for Voice Insights Advanced Features.'
                : ''

            this.$confirm(
                `Are you sure you want to ${action} ${label} for ${this.company.name}?` + warning,
                'Confirm change',
                {
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No',
                    type: 'warning',
                }
            ).then(() => {
                this.updating = true
                axios.post(`/api/admin/company/${this.company.id}/twilio-voice-insights`, {
                    [field]: new_value,
                })
                    .then(response => {
                        this.settings = response.data
                        this.$notify({
                            title: 'Success',
                            message: `${label} ${new_value ? 'enabled' : 'disabled'}`,
                            type: 'success',
                        })
                    })
                    .catch(error => {
                        this.$notify.error({
                            title: 'Error',
                            message: error.response?.data?.message || `Failed to ${action} ${label}`,
                        })
                    })
                    .finally(() => {
                        this.updating = false
                    })
            }).catch(() => {})
        },
    },
}
</script>

<style scoped>
.text-item {
    font-size: 16px;
    display: flex;
    align-items: center;
}
.text-item .label {
    font-weight: bold;
    margin-right: 10px;
    min-width: 180px;
}
.text-item .value {
    color: #409EFF;
}
.text-muted {
    font-size: 13px;
    color: #909399;
}
</style>
